import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { styled } from "@mui/material";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import InputTextField from "../../../../../components/input/inputTextField";
import {
  ButtonDel,
  ButtonOutlined,
  ButtonPrimary,
} from "../../../../../components/button/ButtonCustoms";
import { alertError, alertSuccess } from "../../../../../components/alert/swal";
import { leave_type_approve, OnlineLeaveSubmissionApi } from "../../../../../api/online-leave-submission/online-leave-submission.api";
import {
  setLoading,
  unsetLoading,
} from "../../../../../store/loading/loading.slice";
import ModalCustom from "../../../../../components/modal/ModalCustom";
import RadioCustom from "../../../../../components/radio/RadioCustom";

enum EmpBeforeStartWork {
  FIVE_YEARS_OR_MORE = "FIVE_YEARS_OR_MORE", // ไม่น้อยกว่าห้าปี
  TWO_YEARS_OR_MORE = "TWO_YEARS_OR_MORE",   // ไม่น้อยกว่าสองปี
}

enum AssignmentInternationalType2 {
  RETURNED_TWO_YEARS = "RETURNED_TWO_YEARS",                                     // กลับมาปฏิบัติงานครบสองปีแล้ว
  LESS_THAN_TWO_YEARS_REQUEST_EXEMPTION = "LESS_THAN_TWO_YEARS_REQUEST_EXEMPTION", // ไม่ครบสองปี ขอยกเว้น/ผ่อนผัน
}

enum PerformType2Work {
  AGE_NOT_OVER_52 = "AGE_NOT_OVER_52",             // อายุไม่เกิน ๕๒ ปีบริบูรณ์
  AGE_OVER_52_REQUEST_EXEMPTION = "AGE_OVER_52_REQUEST_EXEMPTION", // อายุเกิน ขอยกเว้น/ผ่อนผัน
}

enum DoesUniversityPlans {
  YES = "YES", // มี
  NO = "NO",   // ไม่มี
}

type CustomCommentApprove = {
  emp_before_start_work: string;
  assignment_international_type_2: string;
  perform_type_2_work: string;
  does_university_plans: string;
  beneficial_thailand: string;
};

type Props = {
  title: string;
  isOpenModal: boolean;
  setIsOpenModal: (isOpen: boolean) => void;
  commentApprove: {
    comment: string;
  };
  setCommentApprove: (input: { comment: string }) => void;
  actionsAllowed: string[];
  onFetch: () => void;
  form_id: string;
  customCommentApprove: CustomCommentApprove;
  setCustomCommentApprove: (input: CustomCommentApprove) => void;
  id_leave_period: number;
};

const ModalConfirmApprove: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const {
    isOpenModal,
    setIsOpenModal,
    title,
    commentApprove,
    setCommentApprove,
    actionsAllowed,
    onFetch,
    form_id,
    customCommentApprove,
    setCustomCommentApprove,
    id_leave_period
  } = props;
  const dispatch = useDispatch();

  const [errorMessage, setErrorMessage] = useState<string>("");

  const handleChange = (name: string, value: string) => {
    setErrorMessage("");
    setCommentApprove({ ...commentApprove, [name]: value });
  };

  const handleSubmit = async (id_leave: number, action_type: string) => {
    // Determine the leave status based on the action type
    const leave_status =
      action_type === "approve"
        ? actionsAllowed[0]
        : action_type === "reject"
          ? actionsAllowed[1]
          : "-";

    // Create the request body with conditional comment fields
    let body: leave_type_approve = {
      leave_status,
      id_leave_period: id_leave_period,
      modified_date: new Date().toISOString(),
    };


    if (
      leave_status === "approve first" ||
      leave_status === "not approve first"
    ) {
      body.leave_approve_first_comment = commentApprove.comment;
      if (leave_status === "approve first") {
        body.emp_before_start_work = customCommentApprove.emp_before_start_work;
        body.assignment_international_type_2 = customCommentApprove.assignment_international_type_2;
        body.perform_type_2_work = customCommentApprove.perform_type_2_work;
        body.does_university_plans = customCommentApprove.does_university_plans;
        body.beneficial_thailand = customCommentApprove.beneficial_thailand;
      }
    } else if (
      leave_status === "approve second" ||
      leave_status === "not approve second"
    ) {
      body.leave_approve_second_comment = commentApprove.comment;
    } else if (
      leave_status === "approve third" ||
      leave_status === "not approve third"
    ) {
      body.leave_approve_third_comment = commentApprove.comment;
    }

    // console.log(body,'body')
    dispatch(setLoading());
    await OnlineLeaveSubmissionApi.approveLeaveRequestWithId(id_leave, body)
      .then((res) => {
        if (res.status < 300) {
          dispatch(unsetLoading());
          alertSuccess(
            title === "approve"
              ? t("อนุมัติคำร้องขอลา")
              : title === "reject"
                ? t("ไม่อนุมัติคำร้องขอลา")
                : "",
            title === "approve"
              ? t("อนุมัติคำร้องขอลา เรียบร้อยแล้ว")
              : title === "reject"
                ? t("ไม่อนุมัติคำร้องขอลา เรียบร้อยแล้ว")
                : "",
            undefined,
            () => {
              setIsOpenModal(false);
              setCommentApprove({ comment: "" });
              onFetch();
            }
          );
        }
      })
      .catch((error) => {
        console.log(error);
        alertError(t("บันทึกข้อมูลไม่สำเร็จ"))
      })
      .finally(() => {
        dispatch(unsetLoading());
      })
  };

  const handleChangeComment = (
    name: keyof CustomCommentApprove,
    value: string
  ) => {
    setCustomCommentApprove({
      ...customCommentApprove,
      [name]: value,
    });
  };

  // แสดงแบบฟอร์มประกอบการพิจารณา (แบบ 06) เฉพาะตอนอนุมัติครั้งแรกเท่านั้น
  const isForm06ApproveFirst =
    form_id === "06" &&
    title !== "reject" &&
    actionsAllowed[0] === "approve first";

  return (
    <ModalCustom
      maxWidth={isForm06ApproveFirst ? 1200 : 800}
      title={
        isForm06ApproveFirst
          ? t("ระบุข้อมูลเพื่อประกอบการพิจารณา")
          : title === "approve"
            ? t("อนุมัติคำร้องขอลา")
            : title === "reject"
              ? t("ไม่อนุมัติคำร้องขอลา")
              : ""
      }
      subTitle={
        isForm06ApproveFirst
          ? t("คำชี้แจ้งและคำรับรองของผู้บังคับบัญชาชั้นต้น ขอชี้แจงว่าบุคลากรผู้นี้")
          : title === "approve"
            ? t("ท่านต้องการอนุมัติคำร้องขอลา ใช่หรือไม่")
            : title === "reject"
              ? t("กรุณาระบุหมายเหตุ")
              : ""
      }
      open={isOpenModal}
      onClose={() => {
        setIsOpenModal(false);
      }}
      component={
        <section className="grid grid-cols-12 gap-y-6">
          {isForm06ApproveFirst ? (
            <>
              <article className="col-span-12">
                <RadioCustom
                  heading={t("ระยะเวลาการเป็นบุคลากรก่อนไปปฏิบัติงาน")}
                  value={customCommentApprove.emp_before_start_work}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleChangeComment(
                      "emp_before_start_work",
                      e.target.value
                    );
                  }}
                  dataList={[
                    {
                      label:
                        t("เป็นบุลากรประจำตลอดมาเป็นระยะเวลาติดต่อกันไม่น้อนกว่าห้าปี ก่อนถึงวันที่ได้รับอนุมัติให้ปฏิบัติงาน"),
                      value: EmpBeforeStartWork.FIVE_YEARS_OR_MORE,
                    },
                    {
                      label:
                        t("เป็นบุคลากรประจำตลอดมาเป็นระยะเวลาติดต่อกันไม่น้อยกว่าสองปี ก่อนถึงวันที่ได้รับอนุมัติให้ไปปฎิบัติงาน"),
                      value: EmpBeforeStartWork.TWO_YEARS_OR_MORE,
                    },
                  ]}
                />
                <span className="ms-8">
                  {t("(กรณีปฏิบัติงานในองค์การสหประชาชาติหรือองค์การในเครือขององค์การสหประชาชาติ)")}
                </span>
              </article>
              {/*  */}
              <article className="col-span-12">
                <RadioCustom
                  heading={t("ในกรณีไปปฎิบัติงานครั้งสุดท้ายเป็นการไปปฏิบัติงานในองค์การระหว่างประเทศประเภทที่ ๒")}
                  value={customCommentApprove.assignment_international_type_2}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleChangeComment(
                      "assignment_international_type_2",
                      e.target.value
                    );
                  }}
                  dataList={[
                    {
                      label:
                        t("ได้กลับมาปฏิบัติงานในมหาวิทยาลัยครบสองปีแล้ว นับตั้งแต่วันที่เริ่มกลับเข้าปฎิบัติงาน"),
                      value: AssignmentInternationalType2.RETURNED_TWO_YEARS,
                    },
                    {
                      label:
                        t("มีเวลาปฏิบัติงานไม่ครบสองปี  แต่ประสงค์จะขอยกเว้นหรือผ่อนผัน โดยมีเหตุความจำเป็นตามเอกสารแนบท้าย"),
                      value: AssignmentInternationalType2.LESS_THAN_TWO_YEARS_REQUEST_EXEMPTION,
                    },
                  ]}
                />
              </article>
              {/*  */}
              <article className="col-span-12">
                <RadioCustom
                  heading={t("ผู้ที่จะปฎิบัติงานประเภทที่ ๒")}
                  value={customCommentApprove.perform_type_2_work}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleChangeComment(
                      "perform_type_2_work",
                      e.target.value
                    );
                  }}
                  dataList={[
                    {
                      label:
                        t("อายุไม่เกิน ๕๒ ปีบริบูรณ์ นับถึงวันที่ได้รับอนุมัติให้ไปปฎิบัติงาน"),
                      value: PerformType2Work.AGE_NOT_OVER_52,
                    },
                    {
                      label:
                        t("อายุเกิน แต่ประสงค์จะขอยกเว้นหรือผ่อนผัน โดยมีเหตุความจำเป็นตามเอกสารที่แนบท้าย"),
                      value: PerformType2Work.AGE_OVER_52_REQUEST_EXEMPTION,
                    },
                  ]}
                />
              </article>
              {/*  */}
              <article className="col-span-12">
                <RadioCustom
                  heading={t("มหาวิทยาลัย มีแผนงาน หรือโครงการที่กำหนดให้กลับมาปฏิบัติงานและการใดหรือไม่")}
                  value={customCommentApprove.does_university_plans}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleChangeComment(
                      "does_university_plans",
                      e.target.value
                    );
                  }}
                  dataList={[
                    {
                      label: t("มี"),
                      value: DoesUniversityPlans.YES,
                    },
                    {
                      label: t("ไม่มี"),
                      value: DoesUniversityPlans.NO,
                    },
                  ]}
                />
              </article>

              <article className="col-span-12">
                <InputTextField
                  value={customCommentApprove.beneficial_thailand}
                  helperText={errorMessage}
                  type={"text"}
                  heading={
                    t("เป็นประโยชน์ต่อประเทศไทย หรือต่อการปฎิบัติงานของมหาวิทยาลัยนี้ประการใดบ้าง (โปรดระบุ)")
                  }
                  placeholder={
                    t("เป็นประโยชน์ต่อประเทศไทย หรือต่อการปฎิบัติงานของมหาวิทยาลัยนี้ประการใดบ้าง (โปรดระบุ)")
                  }
                  onchange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChangeComment("beneficial_thailand", e.target.value)
                  }
                  row={3}
                  maxRows={5}
                  disablePaddingMultiline
                  multiline
                  inputHeight={"auto"}
                />
              </article>

              <article className="col-span-12">
                <InputTextField
                  value={commentApprove.comment}
                  helperText={errorMessage}
                  type={"text"}
                  heading={
                    title === "approve"
                      ? t("รายละเอียดประกอบการพิจารณา")
                      : title === "reject"
                        ? t("หมายเหตุ")
                        : ""
                  }
                  placeholder={
                    title === "approve"
                      ? t("ระบุรายละเอียดประกอบการพิจารณา")
                      : title === "reject"
                        ? t("ระบุหมายเหตุ")
                        : ""
                  }
                  onchange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange("comment", e.target.value)
                  }
                  row={3}
                  maxRows={5}
                  disablePaddingMultiline
                  multiline
                  inputHeight={"auto"}
                />
              </article>
            </>
          ) : (
            <article className="col-span-12">
              <InputTextField
                value={commentApprove.comment}
                helperText={errorMessage}
                type={"text"}
                heading={
                  title === "approve"
                    ? t("ความคิดเห็นผู้บังคับบัญชา")
                    : title === "reject"
                      ? t("หมายเหตุ")
                      : ""
                }
                placeholder={
                  title === "approve"
                    ? t("ระบุความคิดเห็นผู้บังคับบัญชา")
                    : title === "reject"
                      ? t("ระบุหมายเหตุ")
                      : ""
                }
                onchange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange("comment", e.target.value)
                }
                row={3}
                maxRows={5}
                disablePaddingMultiline
                multiline
                inputHeight={"auto"}
              />
            </article>
          )}

          <article className="col-span-12 flex items-center justify-center gap-4 my-4">
            <ButtonOutlined
              onClick={() => {
                setIsOpenModal(false);
                setErrorMessage("");
                setCommentApprove({ comment: "" });
              }}
              mode="del"
              buttonName={t("ยกเลิก")}
            />
            {title === "approve" ? (
              <ButtonPrimary
                onClick={() => id && handleSubmit(+id, title)}
                mode="success"
                buttonName={t("ยืนยัน")}
              />
            ) : title === "reject" ? (
              <ButtonDel
                onClick={() => id && handleSubmit(+id, title)}
                buttonName={t("ยืนยัน")}
              />
            ) : null}
          </article>
        </section>
      }
    />
  );
};

export default ModalConfirmApprove;
